/**
 * Shipping Calculator — Shopify Horizon Theme
 * File: assets/shipping-calculator.js
 *
 * Fetches real carrier-calculated rates via:
 *   GET /cart/shipping_rates.json?shipping_address[country]=...&...
 *
 * Requires carrier-calculated shipping enabled on your Shopify plan.
 */

class ShippingCalculator extends HTMLElement {
  constructor() {
    super();

    // Core elements
    this.form        = this.querySelector('#shipping-calculator-form-inner');
    this.results     = this.querySelector('#shipping-calculator-results');
    this.countryEl   = this.querySelector('#shipping-calculator-country');
    this.provinceEl  = this.querySelector('#shipping-calculator-province');
    this.provinceWrap = this.querySelector('#shipping-calculator-province-wrapper');
    this.submitBtn   = this.querySelector('.shipping-calculator__submit');
    this.submitLabel = this.querySelector('.shipping-calculator__submit-label');
    this.submitSpinner = this.querySelector('.shipping-calculator__submit-spinner');

    // Country → provinces map (populated by Shopify's all_country_option_tags)
    this.provinces = {};

    this._bindEvents();
    this._buildProvincesMap();
    this._restoreAddress();
    this._onCountryChange();
  }

  // ─── Events ──────────────────────────────────────────────────────────────

  _bindEvents() {
    this.countryEl.addEventListener('change', () => this._onCountryChange());
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this._calculate();
    });
  }

  

  // ─── Country / Province handling ─────────────────────────────────────────

  /**
   * Reads the <option data-provinces="..."> attributes Shopify renders via
   * all_country_option_tags to build a map of country code → province list.
   */
  _buildProvincesMap() {
    this.countryEl.querySelectorAll('option').forEach((opt) => {
      const raw = opt.dataset.provinces;
      if (raw) {
        try {
          this.provinces[opt.value] = JSON.parse(raw);
        } catch (_) {
          // ignore malformed
        }
      }
    });
  }

  _onCountryChange() {
    const code = this.countryEl.value;
    const list = this.provinces[code];

    if (list && list.length > 0) {
      // Rebuild province options
      this.provinceEl.innerHTML = '';
      list.forEach(([value, label]) => {
        const opt = document.createElement('option');
        opt.value = value;
        opt.textContent = label;
        this.provinceEl.appendChild(opt);
      });
      this.provinceWrap.hidden = false;
    } else {
      this.provinceWrap.hidden = true;
      this.provinceEl.innerHTML = '';
    }
  }

  // ─── API call ─────────────────────────────────────────────────────────────

  async _calculate() {
    const country  = this.countryEl.value.trim();
    const province = this.provinceEl.value.trim();
    const zip      = this.querySelector('#shipping-calculator-zip').value.trim();

    // Basic validation
    if (!country) {
      this._showError(window.shippingCalcStrings?.countryRequired || 'Please select a country.');
      return;
    }

    this._setLoading(true);
    this._clearResults();
    this._saveAddress({ country, province, zip });

    const params = new URLSearchParams({
      'shipping_address[country]': country,
      'shipping_address[province]': province,
      'shipping_address[zip]': zip,
    });

    try {
      const res = await fetch(`/cart/shipping_rates.json?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      });

      if (res.status === 422) {
        // Shopify returns 422 for invalid/unserviceable addresses
        const body = await res.json();
        const msgs = Object.entries(body)
          .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
          .join(' ');
        this._showError(msgs || (window.shippingCalcStrings?.invalidAddress || 'Invalid address. Please check your details and try again.'));
        return;
      }

      if (!res.ok) {
        throw new Error(`Unexpected response: ${res.status}`);
      }

      const data = await res.json();
      const rates = data.shipping_rates;

      if (!rates || rates.length === 0) {
        this._showNoRates();
      } else {
        this._showRates(rates);
      }
    } catch (err) {
      console.error('[ShippingCalculator]', err);
      this._showError(
        window.shippingCalcStrings?.generalError ||
        'Unable to fetch shipping rates. Please try again.'
      );
    } finally {
      this._setLoading(false);
    }
  }

  // ─── Render helpers ───────────────────────────────────────────────────────

  _showRates(rates) {
    const label = window.shippingCalcStrings?.availableRates || 'Shipping options to your address:';

    // Sort cheapest first
    const sorted = [...rates].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));

    const html = `
      <div class="shipping-calculator__rates-header">${label}</div>
      <ul class="shipping-calculator__rates-list" role="list">
        ${sorted.map((rate, i) => this._rateItemHTML(rate, i)).join('')}
      </ul>
    `;
    this.results.innerHTML = html;
    this.results.classList.add('shipping-calculator__results--visible');

   
  }

  _rateItemHTML(rate, index) {
    const price  = parseFloat(rate.price);
    const isFree = price === 0;
    const priceStr = isFree
      ? (window.shippingCalcStrings?.free || 'Free')
      : this._formatMoney(price);


    return `
  <li class="shipping-calculator__rate-item ${index === 0 ? 'shipping-calculator__rate-item--cheapest' : ''}">
    <div class="shipping-calculator__rate-info">
      <span class="shipping-calculator__rate-name">${rate.name}</span>
    </div>
    <span class="shipping-calculator__rate-price ${isFree ? 'shipping-calculator__rate-price--free' : ''}">${priceStr}</span>
  </li>
`;
  }

  _showNoRates() {
    this.results.innerHTML = `
      <div class="shipping-calculator__notice shipping-calculator__notice--info">
        <span class="shipping-calculator__notice-icon">ℹ</span>
        <span>${window.shippingCalcStrings?.noRates || 'No shipping rates available for this address.'}</span>
      </div>
    `;
    this.results.classList.add('shipping-calculator__results--visible');
  }

  _showError(message) {
    this.results.innerHTML = `
      <div class="shipping-calculator__notice shipping-calculator__notice--error" role="alert">
        <span class="shipping-calculator__notice-icon">!</span>
        <span>${message}</span>
      </div>
    `;
    this.results.classList.add('shipping-calculator__results--visible');
  }

  _clearResults() {
    this.results.innerHTML = '';
    this.results.classList.remove('shipping-calculator__results--visible');
  }

  

  // ─── Utilities ────────────────────────────────────────────────────────────

  _setLoading(loading) {
    this.submitBtn.disabled = loading;
    this.submitLabel.hidden = loading;
    this.submitSpinner.hidden = !loading;
    if (loading) {
      this.submitSpinner.removeAttribute('aria-hidden');
    } else {
      this.submitSpinner.setAttribute('aria-hidden', 'true');
    }
  }

  _formatMoney(amount) {
    // Shopify returns prices in the shop's base currency (dollars as string)
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(amount);
    } catch (_) {
      return `${currency} ${parseFloat(amount).toFixed(2)}`;
    }
  }

  _saveAddress(addr) {
    try {
      sessionStorage.setItem('sc_address', JSON.stringify(addr));
    } catch (_) {}
  }

  _restoreAddress() {
    try {
      const saved = JSON.parse(sessionStorage.getItem('sc_address') || 'null');
      if (!saved) return;

      // Restore country
      this.countryEl.value = saved.country;
      this._onCountryChange();

      // Restore province (after options are built)
      if (saved.province && this.provinceEl.querySelector(`option[value="${saved.province}"]`)) {
        this.provinceEl.value = saved.province;
      }

      // Restore zip
      const zipEl = this.querySelector('#shipping-calculator-zip');
      if (zipEl && saved.zip) zipEl.value = saved.zip;
    } catch (_) {}
  }
}

customElements.define('shipping-calculator', ShippingCalculator);
